export const COPILOT_ANALYSIS_TIMEOUT_MS = 120000
export const COPILOT_RECOVERY_TIMEOUT_MS = 10000
export const COPILOT_RECOVERY_WINDOW_MS = 95000

export const createCopilotRequestId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID()
  const bytes = window.crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 15) | 64
  bytes[8] = (bytes[8] & 63) | 128
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export const shouldRecoverCopilotAnalysis = (error) => {
  if (error?.code === 'ERR_CANCELED' ||
    error?.response?.data?.code === 'copilot_analysis_storage_unavailable') return false
  return [408, 502, 503, 504].includes(error?.response?.status) ||
    ['ECONNABORTED', 'ETIMEDOUT', 'ERR_NETWORK'].includes(error?.code) ||
    error?.message === 'Network Error'
}

const aborted = () => Object.assign(new Error('Request cancelled'), {code: 'ERR_CANCELED'})
const pause = (milliseconds, signal) => new Promise((resolve, reject) => {
  if (signal?.aborted) { reject(aborted()); return }
  const cancel = () => { clearTimeout(timer); signal?.removeEventListener('abort', cancel); reject(aborted()) }
  const timer = setTimeout(() => { signal?.removeEventListener('abort', cancel); resolve() }, milliseconds)
  signal?.addEventListener('abort', cancel, {once: true})
})

// Every query is cache-only on the server. Never retry the original calculation here.
export const recoverCopilotAnalysis = async ({query, signal, isCurrent, wait = pause,
  windowMs = COPILOT_RECOVERY_WINDOW_MS, intervalMs = 3000, maxAttempts = 33}) => {
  const deadline = Date.now() + windowMs
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (signal?.aborted || !isCurrent()) return null
    const remaining = deadline - Date.now()
    if (remaining <= 0) break
    try {
      const result = await query(Math.min(COPILOT_RECOVERY_TIMEOUT_MS, remaining))
      if (signal?.aborted || !isCurrent()) return null
      if (result?.ok) return result
      if (!result?.pending) return null
    } catch (error) {
      if (signal?.aborted || !isCurrent()) return null
      if (!shouldRecoverCopilotAnalysis(error)) throw error
    }
    if (attempt + 1 < maxAttempts && Date.now() < deadline) {
      await wait(Math.min(intervalMs, Math.max(0, deadline - Date.now())), signal)
    }
  }
  return null
}
